
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { regionService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';
import useDepartment from '../hooks/useDepartment';

const RegionManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  // IT calls this master Regions, BioMedical Entities
  const { region: regionWord, regions: regionsWord } = useDepartment();
  const permissions = getPagePermissions('/admin/regions');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('regionManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
    startDate: '',
    endDate: '',
  }, ['/admin/regions']);
  const { currentPage, itemsPerPage, searchTerm, startDate, endDate } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm   = (v) => setFilters({ searchTerm: v });
  const setStartDate    = (v) => setFilters({ startDate: v });
  const setEndDate      = (v) => setFilters({ endDate: v });

  useEffect(() => {
    setPageTitle(regionsWord);
  }, [setPageTitle, regionsWord]);

  useEffect(() => {
    fetchRegions();
  }, [currentPage, searchTerm, startDate, endDate, itemsPerPage]);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await regionService.getAll(params);
      setRegions(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(`Failed to fetch ${regionsWord.toLowerCase()}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleView = (region) => {
    navigate(`/admin/regions/view/${region.id}`);
  };

  const handleEdit = (region) => {
    navigate(`/admin/regions/edit/${region.id}`);
  };

  const handleAddRegion = () => {
    navigate('/admin/regions/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm(`Are you sure you want to delete this ${regionWord.toLowerCase()}?`);
    if (confirmed) {
      try {
        await regionService.delete(id);
        toast.error(`${regionWord} deleted successfully`);
        fetchRegions();
      } catch (err) {
        setError(err.response?.data?.error || `Failed to delete ${regionWord.toLowerCase()}`);
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (region, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [region.id]: true }));
    
    try {
      await regionService.updateStatus(region.id, newStatus);
      toast.success(`${regionWord} ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the regions list
      await fetchRegions();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to update ${regionWord.toLowerCase()} status`);
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [region.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: `Search ${regionsWord.toLowerCase()}...`
            },
            // dateRange: {
            //   startDate: startDate,
            //   endDate: endDate,
            //   onStartDateChange: handleStartDateChange,
            //   onEndDateChange: handleEndDateChange
            // }
          }}
          addButton={permissions.can_add ? {
            label: `Add ${regionWord}`,
            onClick: handleAddRegion,
            variant: 'primary'
          } : null}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
              columns={[
                { 
                  header: 'S.No', 
                  accessor: 'serial',
                  width: '80px',
                  render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
                },
                { 
                  header: 'Name', 
                  accessor: 'name',
                  width: '180px',
                  render: (value) => (
                    <span className="text-sm text-gray-900" title={value}>
                      {value && value.length > 22 ? `${value.substring(0, 22)}...` : value}
                    </span>
                  )
                },
                { 
                  header: 'Code', 
                  accessor: 'code',
                  width: '120px',
                  render: (value) => (
                    <span className="text-sm font-medium text-blue-700" title={value}>
                      {value}
                    </span>
                  )
                },
                { 
                  header: 'Branches', 
                  accessor: 'branch_names',
                  width: '200px',
                  render: (value) => {
                    if (!value) return 'No branches';
                    const names = value.split(', ');
                    const displayText = names.length > 2 
                      ? `${names.slice(0, 2).join(', ')}...` 
                      : names.join(', ');
                    return (
                      <span className="text-sm text-gray-700" title={value}>
                        {displayText}
                      </span>
                    );
                  }
                },
                {
                  header: 'Branch Count',
                  accessor: 'branch_count',
                  width: '120px',
                  align: 'center',
                  render: (value) => (
                    <span className="text-sm font-medium text-gray-900">
                      {value || 0}
                    </span>
                  )
                },
                {
                  header: 'Created',
                  accessor: 'created_at',
                  width: '120px',
                  render: (value) => formatDateDDMMYYYY(value),
                },
                {
                  header: 'Status',
                  accessor: 'status',
                  width: '100px',
                  align: 'center',
                  render: (value, row) => (
                    <StatusToggle
                      status={value}
                      onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                      loading={updatingStatus[row.id]}
                      disabled={!permissions.can_edit}
                    />
                  )
                },
                {
                  header: 'Actions',
                  accessor: 'actions',
                  width: '100px',
                  align: 'center',
                  actions: [
                    ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                    ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
                  ]
                },
              ]}
              data={permissions.can_list ? regions : []}
              onRowClick={permissions.can_view ? handleView : undefined}
              emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : `No ${regionsWord.toLowerCase()} found`}
              loading={loading}
            />
            
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
            
      </Card>
    </div>
  );
};

export default RegionManager;
